import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, User, Edit, Trash2, History, Search } from 'lucide-react';
import { fleetAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const EMPTY = { name: '', mobile: '', alternate_number: '', license_number: '', license_expiry: '',
  address: '', aadhaar: '', emergency_contact: '', status: 'active', notes: '' };

export default function DriversPage() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const isAdmin = ['admin', 'owner', 'super_admin'].includes(user?.role);
  const canManage = isAdmin || (user?.permissions || []).includes('driver:create');

  const load = useCallback(() => {
    setLoading(true);
    fleetAPI.drivers.list({ search: search || undefined })
      .then(r => setDrivers(r.data?.drivers || []))
      .catch(() => toast.error('Failed to load drivers'))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const u = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Driver name is required'); return; }
    setSaving(true);
    try {
      if (editing) { await fleetAPI.drivers.update(editing, form); toast.success('Driver updated'); }
      else {
        const r = await fleetAPI.drivers.create(form);
        if (r.data?.warning) toast.warning(r.data.warning);
        toast.success('Driver added');
      }
      setShowForm(false); setEditing(null); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const openEdit = (d) => {
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach(k => { f[k] = d[k] ?? EMPTY[k]; });
    setForm(f); setEditing(d.driver_id); setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this driver? Historical LRs will keep their driver details.')) return;
    try { await fleetAPI.drivers.remove(id); toast.success('Driver deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div data-testid="drivers-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2"><User className="w-6 h-6 text-purple-600" /> Driver Master</h1>
          <p className="text-sm text-slate-500 mt-1">Centralized driver records used across LRs and reports.</p>
        </div>
        {canManage && <Button onClick={() => { setForm({ ...EMPTY }); setEditing(null); setShowForm(true); }} data-testid="add-driver-btn"><Plus className="w-4 h-4 mr-1" /> Add Driver</Button>}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input className="form-input pl-9" placeholder="Search by name, mobile, license..." value={search} onChange={e => setSearch(e.target.value)} data-testid="driver-search" />
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr><th>Name</th><th>Mobile</th><th>License No.</th><th>License Expiry</th><th>Emergency Contact</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="text-center py-8 text-slate-400">Loading...</td></tr>
            : drivers.length === 0 ? <tr><td colSpan="7" className="text-center py-8 text-slate-400">No drivers yet. Add your first driver.</td></tr>
            : drivers.map(d => (
              <tr key={d.driver_id} data-testid={`driver-row-${d.driver_id}`}>
                <td className="font-medium">{d.name}</td>
                <td className="font-mono text-xs">{d.mobile || '—'}</td>
                <td className="font-mono text-xs">{d.license_number || '—'}</td>
                <td>
                  {d.license_expiry ? (
                    <span className={d.license_expiry < today ? 'text-rose-600 font-semibold text-xs' : 'text-xs'}>{d.license_expiry}{d.license_expiry < today && ' (expired)'}</span>
                  ) : '—'}
                </td>
                <td className="font-mono text-xs">{d.emergency_contact || '—'}</td>
                <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${d.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{d.status}</span></td>
                <td>
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/drivers/${d.driver_id}`} className="p-1.5 hover:bg-purple-50 rounded" title="Trip History" data-testid={`driver-history-${d.driver_id}`}><History className="w-4 h-4 text-purple-600" /></Link>
                    {canManage && <button onClick={() => openEdit(d)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`driver-edit-${d.driver_id}`}><Edit className="w-4 h-4 text-slate-500" /></button>}
                    {isAdmin && <button onClick={() => remove(d.driver_id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`driver-delete-${d.driver_id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="driver-form-modal">
          <DialogHeader><DialogTitle>{editing ? 'Edit Driver' : 'Add Driver'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label">Driver Name *</label><input className="form-input" value={form.name} onChange={e => u('name', e.target.value)} data-testid="driver-name-input" /></div>
            <div><label className="form-label">Mobile</label><input className="form-input" value={form.mobile} onChange={e => u('mobile', e.target.value)} data-testid="driver-mobile-input" /></div>
            <div><label className="form-label">Alternate Number</label><input className="form-input" value={form.alternate_number} onChange={e => u('alternate_number', e.target.value)} /></div>
            <div><label className="form-label">Emergency Contact</label><input className="form-input" value={form.emergency_contact} onChange={e => u('emergency_contact', e.target.value)} /></div>
            <div><label className="form-label">License Number</label><input className="form-input" value={form.license_number} onChange={e => u('license_number', e.target.value.toUpperCase())} data-testid="driver-license-input" /></div>
            <div><label className="form-label">License Expiry</label><input type="date" className="form-input" value={form.license_expiry} onChange={e => u('license_expiry', e.target.value)} /></div>
            <div><label className="form-label">Aadhaar (optional)</label><input className="form-input" value={form.aadhaar} onChange={e => u('aadhaar', e.target.value)} /></div>
            <div><label className="form-label">Status</label><select className="form-input" value={form.status} onChange={e => u('status', e.target.value)} data-testid="driver-status-select"><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
            <div className="col-span-2"><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e => u('address', e.target.value)} /></div>
            <div className="col-span-2"><label className="form-label">Notes</label><textarea className="form-input min-h-[50px]" value={form.notes} onChange={e => u('notes', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-driver-btn">{saving ? 'Saving...' : editing ? 'Update Driver' : 'Add Driver'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
